import { Box, Chip, Typography } from "@mui/material";
import { NavLink } from "react-router-dom";
import { COLORS } from "../../themes/Base";
import { getLocalTime } from "../../utils/Time";
import useSharedStyles from "../../styles/Shared";
import Tooltip from "../../themes/components/Tooltip";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { updateProject } from "../../api/Project";
import { notify, notifySimple } from "../../themes/components/Notifications";
import useButtonStyles from "../../styles/Buttons";
import isEmpty from "lodash/isEmpty";

interface ProjectHeaderProps {
  project: Project;
  refetch: () => void;
  onEdit?: (event: React.MouseEvent<HTMLButtonElement>, project: Project) => void;
  spacing?: string | number;
  children?: React.ReactNode;
}

const Header: React.FC<ProjectHeaderProps> = ({ project, refetch, onEdit, spacing, children }) => {
  const { classes } = useSharedStyles();
  const { cx } = useButtonStyles();
  const statusClassMap: Record<string, string[]> = {
    running: [classes.tag, classes.tagPositive],
    pending: [classes.tag, classes.tagNeutral],
    closed: [classes.tag, classes.tagNegative],
  };

  const onUpdateSubmit = async (values: ProjectUpdateVars) => {
    try {
      const response = await updateProject(values.id, values);
      const { messages, result } = response.projectUpdate;

      if (!isEmpty(messages)) {
        notify(messages);
      } else if (result) {
        notifySimple(["Project updated"], "success");
        refetch();
      }
    } catch (err) {
      notifySimple(["Error updating project"], "error");
    }
  };

  const generateSettingsCell = (project: Project) => (
    <>
      {project.status !== "RUNNING" && (
        <Tooltip title="Launch" placement="top">
          <IconButton
            onClick={() =>
              void onUpdateSubmit({
                id: project.id,
                input: { status: "RUNNING" },
              })
            }
          >
            <PlayCircleIcon />
          </IconButton>
        </Tooltip>
      )}
      {project.status === "RUNNING" && (
        <Tooltip title="Pause" placement="top">
          <IconButton
            onClick={() =>
              void onUpdateSubmit({
                id: project.id,
                input: { status: "PENDING" },
              })
            }
          >
            <PauseCircleIcon />
          </IconButton>
        </Tooltip>
      )}
      {project.status !== "CLOSED" && (
        <Tooltip title="Close" placement="top">
          <IconButton
            onClick={() =>
              void onUpdateSubmit({
                id: project.id,
                input: { status: "CLOSED" },
              })
            }
          >
            <StopCircleIcon />
          </IconButton>
        </Tooltip>
      )}

      <IconButton
        onClick={(e) => {
          if (onEdit) onEdit(e, project);
        }}
        className={classes.settingsButton}
      >
        <MoreVertIcon sx={{ color: "#4F4F52" }} />
      </IconButton>
    </>
  );
  return (
    <Box sx={{ p: spacing, display: "flex", justifyContent: "space-between" }}>
      {/* Header */}
      <Box sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Chip
            label={project.status.toLowerCase()}
            className={cx(...(statusClassMap[project.status.toLowerCase()] || [classes.tag]))}
          />
          <NavLink className={cx(classes.itemName, "link")} to={`/project/${project.id}/view`}>
            {project.name}
          </NavLink>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            color: COLORS.textSecondary,
          }}
        >
          <Typography sx={{ fontSize: 14 }}>
            Created: {getLocalTime(project.inserted_at).format("DD/MM/YYYY HH:mm:ss")}
          </Typography>
          <Typography sx={{ fontSize: 14 }}>
            By: {project.account.first_name} {project.account.last_name}
          </Typography>
          <Typography sx={{ fontSize: 14 }}>Suppliers: {project.suppliers.length}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {children}
        {generateSettingsCell(project)}
      </Box>
    </Box>
  );
};

export default Header;
